/**
 * Costruzione del record di call-log a partire da una FOTOGRAFIA SINCRONA della
 * chiamata appena terminata.
 *
 * Esiste per un motivo preciso (GAIA-2664). Il payload veniva assemblato *dopo*
 * `await getQueue()`, leggendo lo store vivo: direzione, durata, numero ed esito
 * erano quelli del momento in cui la GET tornava, non quelli della chiamata che
 * era finita. Se in quella finestra partiva la chiamata successiva — ed è
 * esattamente ciò che accade quando l'operatore clicca il verde un istante dopo
 * che lo squillo è cessato, perché quel bottone cambia significato sotto il
 * cursore — la riga della chiamata in ENTRATA veniva scritta con direzione,
 * durata ed esito di quella in USCITA. Dai KPI la chiamata in entrata risultava
 * assente.
 *
 * Catturare i dati prima di qualunque `await` chiude la classe di bug, non la
 * singola istanza: nessun campo del record può più essere riscritto da ciò che
 * accade mentre la lookup della coda è in volo.
 */

import type { CallLogPayload } from "@/api/apiclient/call-log";

/**
 * Dati della chiamata terminata, letti in un unico istante e da quel momento
 * immutabili. `direction` NON viene dallo store ma dal parametro con cui è stata
 * avviata l'osservazione della sessione: è l'unica fonte che non può mentire.
 */
export interface TerminatedCallSnapshot {
  extension: string;
  direction: "incoming" | "outgoing";
  /** Controparte: CallerID per le inbound, numero composto per le outbound. */
  phoneNumber: string;
  durationSeconds: number;
  /** ISO. Per una inbound persa la chiamata non è mai partita: coincide con endTime. */
  startTime: string;
  endTime: string;
  /** Già normalizzato da mapOriginToTerminator: operator | customer | system | unknown. */
  terminatedBy: string;
}

/**
 * Il backend indicizza le righe per interno e controparte: senza uno dei due il
 * record non è attribuibile a nessuno e non va scritto. Sono le stesse due
 * condizioni che il codice verificava inline prima di POSTare.
 */
export function isLoggableCall(snapshot: TerminatedCallSnapshot): boolean {
  return !!snapshot.extension && !!snapshot.phoneNumber;
}

export function buildCallLogPayload(
  snapshot: TerminatedCallSnapshot,
  queue: string | null
): CallLogPayload {
  return {
    extension: snapshot.extension,
    queue,
    direction: snapshot.direction,
    startTime: snapshot.startTime,
    endTime: snapshot.endTime,
    duration: snapshot.durationSeconds,
    customer: snapshot.phoneNumber,
    terminatedBy: snapshot.terminatedBy,
  };
}

interface QueueMemberLike {
  name?: unknown;
  location?: unknown;
}

interface QueueLike {
  queue?: unknown;
  members?: unknown;
}

const asRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === "object" && value !== null ? (value as Record<string, unknown>) : null;

/**
 * Estrae la lista delle code dalla risposta axios di `getQueue()`. Il body è
 * storicamente arrivato sia come `{ data: [...] }` sia come un array nudo, quindi
 * qui si accettano `{ data: { data: [...] } }` e `{ data: [...] }`: è la stessa
 * tolleranza che il codice inline aveva con `response.data.data || response.data`.
 */
export function extractQueueList(queueResponse: unknown): QueueLike[] {
  const root = asRecord(queueResponse);
  if (!root) return [];
  if (Array.isArray(root.data)) return root.data as QueueLike[];
  const nested = asRecord(root.data);
  if (nested && Array.isArray(nested.data)) return nested.data as QueueLike[];
  return [];
}

/**
 * Prima coda di cui l'interno risulta membro, o null.
 *
 * Tollerante sul payload di proposito: arricchire la riga con la coda è
 * accessorio, mentre perdere la riga perché un membro non ha `name` sarebbe un
 * danno — è la registrazione della chiamata che conta.
 */
export function resolveQueueForExtension(queueResponse: unknown, extension: string): string | null {
  if (!extension) return null;

  for (const queue of extractQueueList(queueResponse)) {
    if (!Array.isArray(queue.members)) continue;
    const belongs = (queue.members as QueueMemberLike[]).some(
      (member) => typeof member?.name === "string" && member.name.includes(extension)
    );
    if (belongs) return typeof queue.queue === "string" ? queue.queue : null;
  }

  return null;
}
